import type { ItemFieldRow } from '../compile/item-field-reader';

export interface UserChoiceReaders {
    value(row: ItemFieldRow, key: string): unknown;
    entries(row: ItemFieldRow, key: string): unknown[];
    has(row: ItemFieldRow, key: string): boolean;
    field(row: ItemFieldRow, key: string): unknown;
    uuid(value: unknown): string;
}

/** Catalog discovery is a SELECT. Eligibility and option labels belong to the
 * browser; unsupported authoring specs are named refusals, never empty pools. */
export function userChoiceOptions(layer: string, spec: string, catalog: ItemFieldRow[], read: UserChoiceReaders): Array<{ item_id: string; item_name: string; level: string }> {
    const suffix = layer.replace(/^user_choice_\d+_/, '');
    const type = suffix === 'menta' ? 'menta' : suffix === 'curse' ? 'curse' : 'spell';
    const uuidPattern = /^[0-9A-F]{8}(?:-[0-9A-F]{4}){3}-[0-9A-F]{12}$/i;
    const explicit = uuidPattern.test(spec) ? spec.toUpperCase() : '';
    const tokens = explicit || spec === type || spec === suffix ? [] : spec.split('|').filter(Boolean).map(token => token.split(':'));
    const attackTypes: Record<string, string[]> = {
        spell_melee_attack: ['5740CA49-006E-A7A0-B46B-6DDED6CD546C'],
        spell_ranged_attack: ['DD280A3B-6F1D-4CE9-9EFD-505A74D0C07C'],
        spell_attack: ['5740CA49-006E-A7A0-B46B-6DDED6CD546C', 'DD280A3B-6F1D-4CE9-9EFD-505A74D0C07C'],
    };
    const gates = ['spell_requires_class', 'spell_requires_subclass', 'spell_includes_subclass'];
    const supported = new Set(['spell_level', 'menta_level', 'sphere', 'circle', 'mental_manifestation', 'is_ritual', ...gates, ...Object.keys(attackTypes)]);
    const unsupported = tokens.find(([key]) => !supported.has(key ?? ''));
    if (tokens.some(([key, ceiling]) => ['spell_level', 'menta_level'].includes(key ?? '') && (!ceiling || !Number.isFinite(Number(ceiling))))) throw new Error('unported:user-choice:spec:level');
    if (unsupported) throw new Error(`unported:user-choice:spec:${unsupported[0]}`);
    const idOf = (value: unknown): string => read.uuid(value).trim().toUpperCase();
    const values = (key: string): string[] => tokens.filter(([name]) => name === key).map(([, value]) => {
        const id = String(value ?? '').trim().toUpperCase();
        // Legacy taxonomy tokens match the UUID prefix. Embedded Bending's saved
        // sphere token has a trailing suffix; retain its ordinary arcane pool.
        return ['sphere', 'circle', 'mental_manifestation'].includes(key) ? id.slice(0, 36) : id;
    });
    for (const key of gates) if (values(key).some(id => !uuidPattern.test(id)))
        throw new Error(`unported:user-choice:spec:${key}`);
    const matches = (row: ItemFieldRow, key: string, accepted: string[]): boolean => read.entries(row, key).some(entry => accepted.includes(idOf(entry)));
    return catalog.filter(row => {
        if (read.field(row, 'item_type') !== type) return false;
        // A curated single identity is already an explicit option, as in the native creator.
        if (explicit) return idOf(read.field(row, 'item_id')) === explicit;
        // Requires selects only the named family. Includes adds that subclass's
        // options to the ordinary pool; other class/subclass options stay out.
        for (const [field, requiredKey, includedKey] of [
            ['requires_class', 'spell_requires_class', 'spell_requires_class'],
            ['requires_subclass', 'spell_requires_subclass', 'spell_includes_subclass'],
        ] as const) {
            const required = values(requiredKey), included = values(includedKey);
            if (required.length ? !matches(row, field, required) : read.has(row, field) && !matches(row, field, included)) return false;
        }
        for (const levelKey of ['spell_level', 'menta_level']) {
            const ceiling = tokens.find(([key]) => key === levelKey)?.[1];
            if (ceiling !== undefined && Number(read.value(row, levelKey) ?? 0) > Number(ceiling)) return false;
        }
        if (tokens.some(([key]) => key === 'is_ritual') && !read.has(row, 'spell_ritual')) return false;
        for (const [key, accepted] of Object.entries(attackTypes))
            if (values(key).length && !matches(row, 'roll_type', accepted)) return false;
        for (const [key, target] of [['sphere', 'spell_sphere'], ['circle', 'spell_circle'], ['mental_manifestation', 'menta_mental_manifestation']] as const) {
            const accepted = values(key);
            if (accepted.length && !matches(row, target, accepted)) return false;
        }
        return true;
    }).map(row => ({ item_id: String(read.field(row, 'item_id') ?? ''), item_name: String(read.field(row, 'item_name') ?? ''),
        level: String(read.value(row, 'spell_level') ?? read.value(row, 'menta_level') ?? '') }))
        .sort((a, b) => Number(a.level) - Number(b.level) || a.item_name.toLowerCase().localeCompare(b.item_name.toLowerCase(), 'en'));
}
